/** Phase 8 Slice 4 — Quality dashboard.
 *
 * Renders:
 *  - tenant-wide rolling average overall_score across the last 30 days
 *  - per-agent table (agent_kind, run_count, avg_overall_score,
 *    weakest dimension distribution chips)
 *  - dimension averages per agent
 *
 * Backed by GET /api/v1/quality/aggregate.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import QualitySparkline from "../../components/app/QualitySparkline";

const DIM_LABEL = {
  relevance: "Relevance", accuracy: "Accuracy", brand_fit: "Brand fit",
  completeness: "Completeness", safety: "Safety", clarity: "Clarity",
  actionability: "Actionability",
};

function scoreTone(v) {
  if (v == null) return "text-muted-foreground";
  if (v >= 0.8) return "text-emerald-600";
  if (v >= 0.6) return "text-amber-600";
  return "text-rose-600";
}

function HeroAvg({ value, count }) {
  const t = useT();
  return (
    <Card data-testid="quality-hero-card">
      <CardContent className="pt-6 flex items-baseline gap-6">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("quality.tenant_avg")}
          </div>
          <div className={`text-5xl font-semibold tabular-nums ${scoreTone(value)}`}
               data-testid="quality-hero-avg">
            {value != null ? value.toFixed(2) : "—"}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("quality.scored_runs")}
          </div>
          <div className="text-2xl font-semibold tabular-nums"
               data-testid="quality-hero-count">{count}</div>
        </div>
        <div className="ml-auto text-xs text-muted-foreground">
          {t("quality.period_30d")}
        </div>
      </CardContent>
    </Card>
  );
}

function AgentRow({ row }) {
  const t = useT();
  const avg = row.avg_overall_score;
  const dims = Object.entries(row.avg_by_dimension || {});
  const weakest = Object.entries(row.weakest_dimension_distribution || {})
    .sort((a, b) => b[1] - a[1]);
  return (
    <li className="border rounded-md p-3 space-y-2"
        data-testid={`quality-agent-row-${row.agent_kind}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm">{row.agent_kind}</span>
          <Badge variant="outline" className="font-mono text-[10px]">
            {row.run_count} runs
          </Badge>
        </div>
        <div className={`text-2xl font-semibold tabular-nums ${scoreTone(avg)}`}
             data-testid={`quality-agent-avg-${row.agent_kind}`}>
          {avg != null ? avg.toFixed(2) : "—"}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {dims.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{DIM_LABEL[k] || k}</span>
            <span className={`tabular-nums ${scoreTone(v)}`}>{v.toFixed(2)}</span>
          </div>
        ))}
      </div>
      {weakest.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
          <span className="text-muted-foreground">
            {t("quality.weakest_dist")}:
          </span>
          {weakest.map(([k, n]) => (
            <Badge key={k} variant="secondary" className="font-mono text-[10px]">
              {DIM_LABEL[k] || k} · {n}
            </Badge>
          ))}
        </div>
      )}
    </li>
  );
}

export default function Quality() {
  const t = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/quality/aggregate?include_daily=true");
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.status === 403
          ? t("quality.forbidden") : t("quality.load_failed"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [t]);

  if (loading) return (
    <p className="text-sm text-muted-foreground"
       data-testid="quality-loading">{t("common.loading")}</p>
  );
  if (error) return (
    <p className="text-sm text-rose-600"
       data-testid="quality-error">{error}</p>
  );

  const rows = data?.by_agent || [];
  return (
    <div className="space-y-4" data-testid="quality-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("quality.title")}
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {t("quality.subtitle")}
          </p>
        </div>
        <Link to="/app/dashboard"
              className="text-xs underline text-muted-foreground"
              data-testid="quality-back">{t("common.back")}</Link>
      </div>

      <HeroAvg value={data?.tenant_overall_avg}
               count={rows.reduce((s, r) => s + (r.run_count || 0), 0)} />

      {Array.isArray(data?.daily_series) && data.daily_series.length > 0 && (
        <Card data-testid="quality-sparkline-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("quality.trend_title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <QualitySparkline data={data.daily_series}
                              label={t("quality.trend_label")} />
          </CardContent>
        </Card>
      )}

      <Card data-testid="quality-agents-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {t("quality.per_agent_title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-xs text-muted-foreground"
               data-testid="quality-empty-rows">{t("quality.no_scored_runs")}</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => <AgentRow key={r.agent_kind} row={r} />)}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
